import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { formatCurrency, getUserCurrencySymbol, getUserCountry } from '../utils/countries';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Search, Filter, Sparkles, TrendingUp, FileText, MessageSquare, 
  Settings, LogOut, Briefcase, DollarSign, Clock, ChevronRight,
  Loader2, Send, User, Menu, X, Star, Target, Award, Zap, Plus, Shield,
  BarChart3, BadgeCheck, CheckCircle2
} from 'lucide-react';

// Verified Badge Component
const VerifiedBadge = ({ size = 'sm', showLabel = false }) => {
  const sizeClasses = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="inline-flex items-center gap-1" title="Verified Creator">
      <div className={`${sizeClasses[size]} rounded-full bg-[#3AAFB9] flex items-center justify-center`}>
        <CheckCircle2 className={`${size === 'xs' ? 'w-2.5 h-2.5' : size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white`} />
      </div>
      {showLabel && <span className="text-xs font-medium text-[#3AAFB9]">Verified</span>}
    </div>
  );
};

const NICHES = ['Fashion', 'Fitness', 'Tech', 'Beauty', 'Food', 'Travel', 'Lifestyle', 'Gaming', 'Music', 'Education'];

// Match Score Display Component
const MatchScoreCard = ({ score }) => {
  if (!score) return null;
  
  const getScoreColor = (value) => {
    if (value >= 80) return 'text-green-500';
    if (value >= 60) return 'text-[#3AAFB9]';
    if (value >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };
  
  return (
    <div className="glass-card rounded-2xl p-6 border-[#3AAFB9]/20">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 flex items-center justify-center shadow-lg shadow-[#3AAFB9]/20">
          <Target className="w-5 h-5 text-white" />
        </div>
        <h3 className="font-bold text-gray-900">Your Match Score</h3>
      </div>
      <div className="text-center mb-6">
        <div className={`text-5xl font-bold ${getScoreColor(score.overall_score)}`}>
          {score.overall_score}
        </div>
        <p className="text-sm text-gray-500 mt-1">Overall Score</p>
      </div>
      <div className="space-y-4">
        <ScoreBar label="Audience Relevance" value={score.audience_relevance} icon={<Target className="w-4 h-4" />} color="teal" />
        <ScoreBar label="Engagement Quality" value={score.engagement_quality} icon={<Zap className="w-4 h-4" />} color="coral" />
        <ScoreBar label="Niche Authority" value={score.niche_authority} icon={<Award className="w-4 h-4" />} color="teal" />
        <ScoreBar label="Authenticity" value={score.authenticity_score} icon={<Star className="w-4 h-4" />} color="coral" />
      </div>
      <p className="text-xs text-gray-400 mt-4 text-center">
        Ranked by engagement, not followers — the micro-creator advantage
      </p>
    </div>
  );
};

const ScoreBar = ({ label, value, icon, color = 'teal' }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-gray-600">
        {icon}
        {label}
      </span>
      <span className="font-bold text-gray-900">{value}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-500 ${color === 'coral' ? 'bg-gradient-to-r from-[#E8795A] to-[#E8795A]/70' : 'bg-gradient-to-r from-[#3AAFB9] to-[#3AAFB9]/70'}`}
        style={{ width: `${value}%` }}
      />
    </div>
  </div>
);

// Analytics Input Dialog
const AnalyticsInputDialog = ({ open, onOpenChange, onSave, currentAnalytics, currencySymbol = '$' }) => {
  const [formData, setFormData] = useState({
    follower_count: currentAnalytics?.follower_count || '',
    avg_views: currentAnalytics?.avg_views || '',
    avg_likes: currentAnalytics?.avg_likes || '',
    avg_comments: currentAnalytics?.avg_comments || '',
    engagement_rate: currentAnalytics?.engagement_rate || '',
    rate_per_post: currentAnalytics?.rate_per_post || ''
  });
  const [saving, setSaving] = useState(false);

  // Update form when currentAnalytics changes
  useEffect(() => {
    if (currentAnalytics) {
      setFormData({
        follower_count: currentAnalytics.follower_count || '',
        avg_views: currentAnalytics.avg_views || '',
        avg_likes: currentAnalytics.avg_likes || '',
        avg_comments: currentAnalytics.avg_comments || '',
        engagement_rate: currentAnalytics.engagement_rate || '',
        rate_per_post: currentAnalytics.rate_per_post || ''
      });
    }
  }, [currentAnalytics]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  // Auto-calculate engagement rate
  const calculateEngagement = () => {
    const followers = parseFloat(formData.follower_count) || 0;
    const likes = parseFloat(formData.avg_likes) || 0;
    const comments = parseFloat(formData.avg_comments) || 0;
    if (followers > 0) {
      const rate = ((likes + comments) / followers * 100).toFixed(2);
      setFormData(prev => ({ ...prev, engagement_rate: rate }));
    }
  };

  // Auto-calculate estimated post value based on industry benchmarks
  const calculatePostValue = () => {
    const followers = parseFloat(formData.follower_count) || 0;
    const engagementRate = parseFloat(formData.engagement_rate) || 0;
    // Simple formula: base rate + engagement multiplier
    let baseRate = 50;
    if (followers >= 10000) baseRate = 100;
    if (followers >= 25000) baseRate = 200;
    if (followers >= 50000) baseRate = 400;
    if (followers >= 100000) baseRate = 800;
    const engagementBonus = engagementRate > 5 ? baseRate * 0.5 : engagementRate > 3 ? baseRate * 0.25 : 0;
    const estimatedValue = Math.round(baseRate + engagementBonus);
    setFormData(prev => ({ ...prev, rate_per_post: estimatedValue }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#E8795A]" />
            Add Your Analytics
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Enter your social media stats to unlock your Match Score and get better brand recommendations.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="follower_count">Total Followers</Label>
              <Input
                id="follower_count"
                type="number"
                placeholder="e.g., 15000"
                value={formData.follower_count}
                onChange={(e) => setFormData(prev => ({ ...prev, follower_count: e.target.value }))}
                onBlur={calculateEngagement}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="avg_views">Avg Views per Post</Label>
              <Input
                id="avg_views"
                type="number"
                placeholder="e.g., 5000"
                value={formData.avg_views}
                onChange={(e) => setFormData(prev => ({ ...prev, avg_views: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="avg_likes">Avg Likes per Post</Label>
              <Input
                id="avg_likes"
                type="number"
                placeholder="e.g., 800"
                value={formData.avg_likes}
                onChange={(e) => setFormData(prev => ({ ...prev, avg_likes: e.target.value }))}
                onBlur={calculateEngagement}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="avg_comments">Avg Comments per Post</Label>
              <Input
                id="avg_comments"
                type="number"
                placeholder="e.g., 50"
                value={formData.avg_comments}
                onChange={(e) => setFormData(prev => ({ ...prev, avg_comments: e.target.value }))}
                onBlur={calculateEngagement}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t">
            <div className="space-y-2">
              <Label htmlFor="engagement_rate" className="flex items-center gap-2">
                Engagement Rate %
                <span className="text-xs text-muted-foreground">(auto-calculated)</span>
              </Label>
              <Input
                id="engagement_rate"
                type="number"
                step="0.01"
                placeholder="e.g., 5.5"
                value={formData.engagement_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, engagement_rate: e.target.value }))}
                onBlur={calculatePostValue}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_per_post" className="flex items-center gap-2">
                Your Rate ({currencySymbol}/post)
                <span className="text-xs text-muted-foreground">(suggested)</span>
              </Label>
              <Input
                id="rate_per_post"
                type="number"
                placeholder="e.g., 150"
                value={formData.rate_per_post}
                onChange={(e) => setFormData(prev => ({ ...prev, rate_per_post: e.target.value }))}
              />
            </div>
          </div>

          <div className="bg-[#3AAFB9]/10 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>💡 Tip:</strong> Micro-creators (1K-50K followers) with high engagement rates 
              often get better brand deals than larger accounts with low engagement. Focus on quality!
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#3AAFB9] hover:bg-[#2F9AA3]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Analytics
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Analytics Card for Creators
const AnalyticsCard = ({ analytics, onEditClick, currencySymbol = '$' }) => {
  if (!analytics || Object.keys(analytics).length === 0 || !analytics.engagement_rate) {
    return (
      <Card 
        className="cursor-pointer hover:border-[#E8795A]/50 transition-colors"
        onClick={onEditClick}
      >
        <CardContent className="py-6 text-center">
          <TrendingUp className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground font-medium">Complete your profile to see analytics</p>
          <p className="text-xs text-muted-foreground mt-1">Click here to add your social stats</p>
          <Button variant="outline" size="sm" className="mt-3">
            <Plus className="w-4 h-4 mr-2" />
            Add Analytics
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#E8795A]" />
            Your Analytics
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onEditClick}>
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold">{analytics.engagement_rate}%</div>
            <p className="text-xs text-muted-foreground">Engagement Rate</p>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold">{currencySymbol}{(analytics.estimated_post_value || analytics.rate_per_post || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Est. Post Value</p>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold">{analytics.past_campaigns_count || 0}</div>
            <p className="text-xs text-muted-foreground">Past Campaigns</p>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold">{analytics.completion_rate || 100}%</div>
            <p className="text-xs text-muted-foreground">Completion Rate</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('discover');
  const [campaigns, setCampaigns] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [matchScore, setMatchScore] = useState(null);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [nicheFilter, setNicheFilter] = useState('');
  const [aiMatches, setAiMatches] = useState([]);
  const [aiInsights, setAiInsights] = useState('');
  const [matchingLoading, setMatchingLoading] = useState(false);

  // Proposal Dialog
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [proposalData, setProposalData] = useState({
    pitch: '',
    proposed_rate: 0,
    deliverables: '',
    timeline: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMatchScore = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const response = await api.get(`/matching/creator-score/${user.user_id}`);
      setMatchScore(response.data.match_score);
    } catch (error) {
      // Match score not critical, silently fail
      console.log('Match score not available');
    }
  }, [user?.user_id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get user's country for filtering
      const countryCode = user?.location?.country_code || user?.creator_profile?.country_code || '';
      
      const [campaignsRes, proposalsRes, statsRes] = await Promise.all([
        countryCode 
          ? api.get(`/campaigns/by-country/${countryCode}`)
          : api.get('/campaigns/'),
        api.get('/proposals/my'),
        api.get('/stats')
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setProposals(proposalsRes.data.proposals || []);
      setStats(statsRes.data || {});
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user?.location?.country_code, user?.creator_profile?.country_code]);

  useEffect(() => {
    fetchData();
    fetchMatchScore();
  }, [fetchData, fetchMatchScore]);

  const handleSaveAnalytics = async (analyticsData) => {
    // Calculate estimated post value
    const followers = parseFloat(analyticsData.follower_count) || 0;
    const engagementRate = parseFloat(analyticsData.engagement_rate) || 0;
    let estimatedValue = parseFloat(analyticsData.rate_per_post) || 50;
    
    const analytics = {
      avg_views: parseInt(analyticsData.avg_views) || 0,
      engagement_rate: parseFloat(analyticsData.engagement_rate) || 0,
      avg_likes: parseInt(analyticsData.avg_likes) || 0,
      avg_comments: parseInt(analyticsData.avg_comments) || 0,
      estimated_post_value: estimatedValue,
      past_campaigns_count: user?.creator_profile?.analytics?.past_campaigns_count || 0,
      avg_ctr: 0,
      completion_rate: 100,
      avg_response_time_hours: 24,
      follower_count: parseInt(analyticsData.follower_count) || 0
    };

    const updatedProfile = {
      ...user.creator_profile,
      follower_count: parseInt(analyticsData.follower_count) || 0,
      rate_per_post: parseFloat(analyticsData.rate_per_post) || 0,
      analytics: analytics
    };

    try {
      const response = await api.put('/users/profile', { creator_profile: updatedProfile });
      
      // Update local user state
      const updatedUser = {
        ...user,
        creator_profile: updatedProfile
      };
      updateUser(updatedUser);
      
      // Refresh match score after saving analytics
      setTimeout(() => fetchMatchScore(), 500);
      
      toast.success('Analytics saved successfully!');
    } catch (error) {
      console.error('Failed to save analytics:', error);
      toast.error('Failed to save analytics');
      throw error;
    }
  };

  // Get user's currency
  const userCurrency = getUserCurrencySymbol(user);
  const userCountry = getUserCountry(user);

  const fetchFilteredCampaigns = async () => {
    try {
      const userCountry = user?.location?.country_code || user?.creator_profile?.country_code || '';
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (nicheFilter) params.append('niche', nicheFilter);
      if (userCountry) params.append('country_code', userCountry);
      
      const response = await api.get(`/campaigns/?${params.toString()}`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to search campaigns');
    }
  };

  const handleAiMatching = async () => {
    setMatchingLoading(true);
    try {
      const response = await api.post('/matching/campaigns', {
        niches: user?.creator_profile?.niches || [],
        follower_preference: user?.creator_profile?.follower_range || ''
      });
      setAiMatches(response.data.matches || []);
      setAiInsights(response.data.ai_insights || '');
      toast.success('AI matching complete!');
    } catch (error) {
      toast.error('AI matching failed');
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleSubmitProposal = async () => {
    if (!selectedCampaign) return;
    setSubmitting(true);
    try {
      await api.post('/proposals', {
        campaign_id: selectedCampaign.campaign_id,
        ...proposalData
      });
      toast.success('Proposal submitted!');
      setSelectedCampaign(null);
      setProposalData({ pitch: '', proposed_rate: 0, deliverables: '', timeline: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const displayCampaigns = aiMatches.length > 0 ? aiMatches : campaigns;

  if (loading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="glass-card p-8 rounded-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-pink" style={{ top: '-10%', right: '5%', opacity: 0.4 }} />
        <div className="creator-orb creator-orb-orange" style={{ bottom: '10%', left: '-5%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-teal" style={{ top: '40%', right: '-10%', opacity: 0.35 }} />
        
        {/* Network Lines */}
        <div className="network-lines opacity-30" />
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 glass-sidebar z-50 transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full relative z-10">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png" 
                alt="CmdOut" 
                className="w-10 h-10"
              />
              <div>
                <span className="font-bold text-lg tracking-tight">CmdOut</span>
                <span className="text-[#3AAFB9] font-bold">.io</span>
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 mx-4 glass-card rounded-xl mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8795A] to-[#E8795A]/70 flex items-center justify-center overflow-hidden shadow-lg shadow-[#E8795A]/20">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold truncate text-gray-900">{user?.name}</p>
                  {user?.creator_profile?.verified && <VerifiedBadge size="sm" />}
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#E8795A] animate-pulse"></div>
                  <p className="text-xs text-[#E8795A] font-medium">Creator</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1">
            <NavItem icon={<Briefcase />} label="Discover" active={activeTab === 'discover'} onClick={() => setActiveTab('discover')} />
            <NavItem icon={<Sparkles />} label="AI Matches" active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} accent="coral" />
            <NavItem icon={<FileText />} label="My Proposals" active={activeTab === 'proposals'} onClick={() => setActiveTab('proposals')} />
            <NavItem icon={<Shield />} label="Contracts" onClick={() => navigate('/contracts')} />
            <NavItem icon={<BarChart3 />} label="Analytics" onClick={() => navigate('/creator/analytics')} />
            <NavItem icon={<BadgeCheck />} label="Verification" onClick={() => navigate('/creator/verification')} />
            <NavItem icon={<MessageSquare />} label="Messages" onClick={() => navigate('/creator/messages')} />
            <NavItem icon={<Settings />} label="Settings" onClick={() => navigate('/creator/settings')} />
          </nav>

          {/* Logout */}
          <div className="p-4 mx-4 mb-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-gray-500 hover:text-gray-900 hover:bg-red-50 rounded-xl transition-all group"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 group-hover:text-red-500" />
              <span className="group-hover:text-red-500">Log out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen relative z-10">
        {/* Header */}
        <header className="sticky top-0 glass-nav z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {activeTab === 'discover' && 'Discover Campaigns'}
                  {activeTab === 'ai' && 'AI Matched Campaigns'}
                  {activeTab === 'proposals' && 'My Proposals'}
                </h1>
                <p className="text-sm text-gray-500">Find your next collaboration opportunity</p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Match Score and Stats Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Match Score Card */}
            <div className="lg:col-span-1">
              <MatchScoreCard score={matchScore} />
            </div>
            
            {/* Stats Cards */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Proposals" value={stats.total_proposals || 0} icon={<FileText />} />
                <StatCard label="Accepted" value={stats.accepted_proposals || 0} icon={<TrendingUp />} color="coral" />
                <StatCard label="Active Contracts" value={stats.active_contracts || 0} icon={<Briefcase />} />
                <StatCard label="Total Earnings" value={`$${stats.total_earnings || 0}`} icon={<DollarSign />} color="primary" />
              </div>
              {/* Analytics Preview */}
              <div className="mt-4">
                <AnalyticsCard 
                  analytics={user?.creator_profile?.analytics} 
                  onEditClick={() => setAnalyticsDialogOpen(true)}
                  currencySymbol={userCurrency}
                />
              </div>
            </div>
          </div>

          {/* Analytics Dialog */}
          <AnalyticsInputDialog
            open={analyticsDialogOpen}
            onOpenChange={setAnalyticsDialogOpen}
            onSave={handleSaveAnalytics}
            currentAnalytics={{
              ...user?.creator_profile?.analytics,
              follower_count: user?.creator_profile?.follower_count,
              rate_per_post: user?.creator_profile?.rate_per_post
            }}
            currencySymbol={userCurrency}
          />

          {/* Discover Tab */}
          {activeTab === 'discover' && (
            <div>
              {/* Search & Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search campaigns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchFilteredCampaigns()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Select value={nicheFilter || "all"} onValueChange={(v) => setNicheFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full md:w-48" data-testid="niche-filter">
                    <SelectValue placeholder="All Niches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Niches</SelectItem>
                    {NICHES.map(niche => (
                      <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchFilteredCampaigns} variant="outline" data-testid="search-btn">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
              </div>

              {/* Campaigns Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayCampaigns.map(campaign => (
                  <CampaignCard 
                    key={campaign.campaign_id} 
                    campaign={campaign}
                    onApply={() => setSelectedCampaign(campaign)}
                  />
                ))}
                {displayCampaigns.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    No campaigns found. Check back later!
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Matches Tab */}
          {activeTab === 'ai' && (
            <div>
              <Card className="mb-6 border-[#E8795A]/30 bg-[#E8795A]/5">
                <CardContent className="py-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">AI-Powered Matching</h3>
                      <p className="text-muted-foreground">
                        Let our AI find the best campaigns for your profile and niche.
                      </p>
                    </div>
                    <Button 
                      onClick={handleAiMatching}
                      disabled={matchingLoading}
                      className="bg-[#E8795A] text-white hover:bg-[#D76A4C]"
                      data-testid="ai-match-btn"
                    >
                      {matchingLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Find Matches
                    </Button>
                  </div>
                  {aiInsights && (
                    <div className="mt-4 p-4 bg-background/50 rounded-lg">
                      <p className="text-sm">{aiInsights}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiMatches.map(campaign => (
                  <CampaignCard 
                    key={campaign.campaign_id} 
                    campaign={campaign}
                    onApply={() => setSelectedCampaign(campaign)}
                    highlight
                  />
                ))}
                {aiMatches.length === 0 && !matchingLoading && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    Click "Find Matches" to get AI-powered campaign recommendations.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Proposals Tab */}
          {activeTab === 'proposals' && (
            <div className="space-y-4">
              {proposals.map(proposal => (
                <ProposalCard key={proposal.proposal_id} proposal={proposal} />
              ))}
              {proposals.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No proposals yet. Start applying to campaigns!
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Apply Dialog */}
      <Dialog open={!!selectedCampaign} onOpenChange={() => setSelectedCampaign(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply to: {selectedCampaign?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Your Pitch</Label>
              <Textarea
                placeholder="Why are you a great fit for this campaign?"
                value={proposalData.pitch}
                onChange={(e) => setProposalData(prev => ({ ...prev, pitch: e.target.value }))}
                rows={4}
                data-testid="pitch-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Proposed Rate ($)</Label>
              <Input
                type="number"
                placeholder="Your rate for this campaign"
                value={proposalData.proposed_rate || ''}
                onChange={(e) => setProposalData(prev => ({ ...prev, proposed_rate: parseFloat(e.target.value) || 0 }))}
                data-testid="rate-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Deliverables</Label>
              <Input
                placeholder="e.g., 2 Instagram posts, 1 story"
                value={proposalData.deliverables}
                onChange={(e) => setProposalData(prev => ({ ...prev, deliverables: e.target.value }))}
                data-testid="deliverables-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Timeline</Label>
              <Input
                placeholder="e.g., 1 week"
                value={proposalData.timeline}
                onChange={(e) => setProposalData(prev => ({ ...prev, timeline: e.target.value }))}
                data-testid="timeline-input"
              />
            </div>
            <Button 
              onClick={handleSubmitProposal}
              disabled={submitting || !proposalData.pitch}
              className="w-full"
              data-testid="submit-proposal-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Proposal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NavItem = ({ icon, label, active, onClick, accent }) => (
  <button
    onClick={onClick}
    className={`nav-item flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all ${
      active 
        ? accent === 'coral' 
          ? 'active bg-[#E8795A]/10 text-[#E8795A] font-medium' 
          : 'active bg-[#3AAFB9]/10 text-[#3AAFB9] font-medium'
        : 'text-gray-600 hover:text-gray-900'
    }`}
    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
  >
    <span className={active ? '' : 'opacity-70'}>{icon}</span>
    <span>{label}</span>
    {active && (
      <div className={`ml-auto w-2 h-2 rounded-full ${accent === 'coral' ? 'bg-[#E8795A]' : 'bg-[#3AAFB9]'}`} />
    )}
  </button>
);

const StatCard = ({ label, value, icon, color }) => (
  <div className="glass-card stat-card rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color === 'coral' ? 'text-[#E8795A]' : color === 'primary' ? 'text-[#3AAFB9]' : 'text-gray-900'}`}>
          {value}
        </p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
        color === 'coral' ? 'bg-gradient-to-br from-[#E8795A] to-[#E8795A]/70 text-white shadow-[#E8795A]/20' : 
        color === 'primary' ? 'bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 text-white shadow-[#3AAFB9]/20' : 
        'bg-gradient-to-br from-gray-100 to-gray-50 text-gray-500'
      }`}>
        {icon}
      </div>
    </div>
  </div>
);

const CampaignCard = ({ campaign, onApply, highlight }) => {
  const navigate = useNavigate();
  
  return (
    <Card className={`card-hover cursor-pointer ${highlight ? 'border-[#E8795A]/50' : ''}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {campaign.brand_logo ? (
              <img src={campaign.brand_logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="font-medium">{campaign.brand_name || 'Brand'}</p>
              <p className="text-xs text-muted-foreground">
                {campaign.applications_count} applicants
              </p>
            </div>
          </div>
          {highlight && (
            <Badge className="bg-[#E8795A] text-white">AI Match</Badge>
          )}
        </div>
        
        <h3 className="font-semibold text-lg mb-2 line-clamp-2" onClick={() => navigate(`/campaign/${campaign.campaign_id}`)}>
          {campaign.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {campaign.description}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {campaign.niches?.slice(0, 3).map(niche => (
            <Badge key={niche} variant="secondary" className="text-xs">
              {niche}
            </Badge>
          ))}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-[#3AAFB9]" />
            <span>
              {campaign.currency_symbol || userCurrency}{campaign.budget_min?.toLocaleString()} - {campaign.currency_symbol || userCurrency}{campaign.budget_max?.toLocaleString()}
            </span>
            {campaign.country_code && (
              <Badge variant="outline" className="text-xs ml-2">
                {campaign.country_code}
              </Badge>
            )}
          </div>
          <Button size="sm" onClick={onApply} className="bg-[#E8795A] hover:bg-[#D76A4C]" data-testid={`apply-${campaign.campaign_id}`}>
            Apply
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ProposalCard = ({ proposal }) => {
  const navigate = useNavigate();
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500',
    accepted: 'bg-green-500/20 text-green-500',
    rejected: 'bg-red-500/20 text-red-500',
    completed: 'bg-primary/20 text-primary'
  };

  return (
    <Card className="card-hover">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 
                className="font-semibold cursor-pointer hover:text-primary transition-colors"
                onClick={() => navigate(`/campaign/${proposal.campaign_id}`)}
              >
                {proposal.campaign_title || 'Campaign'}
              </h3>
              <Badge className={statusColors[proposal.status]}>
                {proposal.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1">{proposal.pitch}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                ${proposal.proposed_rate}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {new Date(proposal.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/campaign/${proposal.campaign_id}`)}>
            View
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreatorDashboard;
